import api from "Common/api";
import { AssistantConversation, AssistantConversationSummary, AssistantReply, SupportTicketDraft } from "./types";
import { LoganProjectDraft, LoganSuggestedMilestone } from "./projectDraft";

interface ApiResult<T> {
  success: boolean;
  conversation?: T;
  reply?: T;
  conversations?: T;
  draft?: T;
}

export interface ContractWorkflowChange {
  field: keyof LoganProjectDraft | string;
  label: string;
  value: string | boolean | LoganSuggestedMilestone[];
  source: "user_message" | "ai_suggestion";
  confidence: "high" | "medium" | "low";
}

export interface ContractWorkflowTurn {
  handled: boolean;
  intent?: "question" | "answer" | "instruction";
  message: string;
  proposedChanges: ContractWorkflowChange[];
  nextField: string;
  nextQuestion: string;
}

export async function prepareContractWorkflowTurn(
  message: string,
  snapshot: LoganProjectDraft,
  expectedField = "",
  editableFields: string[] = []
): Promise<ContractWorkflowTurn> {
  const result = await api.post<any, { turn?: ContractWorkflowTurn }>(
    "/v1/api/product-assistant/contract-workflow/turn",
    { message, snapshot, expectedField, editableFields }
  );
  if (!result.turn) throw new Error("Logan could not prepare the form update.");
  return result.turn;
}

export async function listConversations(search = ""): Promise<AssistantConversationSummary[]> {
  const result = await api.get<any, ApiResult<AssistantConversationSummary[]>>(
    `/v1/api/product-assistant/conversations${search ? `?search=${encodeURIComponent(search)}` : ""}`
  );
  return result.conversations || [];
}

export async function getConversation(conversationId: string): Promise<AssistantConversation> {
  const result = await api.get<any, ApiResult<AssistantConversation>>(`/v1/api/product-assistant/conversations/${conversationId}`);
  if (!result.conversation) throw new Error("Conversation was not found.");
  return result.conversation;
}

export async function renameConversation(conversationId: string, title: string): Promise<void> {
  await api.patch(`/v1/api/product-assistant/conversations/${conversationId}`, { title });
}

export async function getCurrentConversation(): Promise<AssistantConversation | null> {
  const result = await api.get<any, ApiResult<AssistantConversation>>(
    "/v1/api/product-assistant/conversations/current"
  );
  return result.conversation || null;
}

export async function createConversation(locale: string, brandName = "RELiBUILD"): Promise<AssistantConversation> {
  const result = await api.post<any, ApiResult<AssistantConversation>>(
    "/v1/api/product-assistant/conversations",
    { locale }
  );
  if (!result.conversation) throw new Error("Product guide conversation was not created.");
  return { ...result.conversation, title: `Logan — ${brandName} Product Guide`, messages: [] };
}

export async function sendAssistantMessage(
  conversationId: string,
  message: string,
  pageContext: { route: string; module: string; entityId: string; currentField?: string; contextSummary?: string }
): Promise<AssistantReply> {
  const result = await api.post<any, ApiResult<AssistantReply>>(
    `/v1/api/product-assistant/conversations/${conversationId}/messages`,
    { message, pageContext }
  );
  if (!result.reply) throw new Error("Product guide did not return a reply.");
  return result.reply;
}

export async function transcribeAssistantAudio(audio: Blob, extension = "webm"): Promise<string> {
  const form = new FormData();
  form.append("audio", audio, `logan-voice.${extension}`);
  form.append("locale", navigator.language || "en-US");
  const result = await api.post<any, { success: boolean; transcript?: string }>(
    "/v1/api/product-assistant/transcriptions",
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  const transcript = result.transcript?.trim();
  if (!transcript) throw new Error("No speech was detected.");
  return transcript;
}

export async function deleteConversation(conversationId: string): Promise<void> {
  await api.delete(`/v1/api/product-assistant/conversations/${conversationId}`);
}

export async function saveAssistantFeedback(
  messageId: string,
  rating: "helpful" | "not_helpful",
  reason = "",
  comment = ""
): Promise<void> {
  await api.post(`/v1/api/product-assistant/messages/${messageId}/feedback`, { rating, reason, comment });
}

export async function draftSupportTicket(
  conversationId: string,
  pageContext: { route: string; module: string; entityId: string }
): Promise<SupportTicketDraft> {
  const result = await api.post<any, ApiResult<SupportTicketDraft>>(
    `/v1/api/product-assistant/conversations/${conversationId}/tickets/draft`, { pageContext }
  );
  if (!result.draft) throw new Error("Ticket draft was not prepared.");
  return result.draft;
}

export interface LoganFeedbackMetric { rating: "helpful" | "not_helpful"; reason: string; total: number; }
export interface LoganKnowledgeArticle {
  id?: number; slug: string; title: string; module: string; content: string; route: string;
  userTypes: string[]; requiredPermissions: string[]; keywords: string[];
  status: "draft" | "published" | "archived"; version?: number; updatedAt?: string;
  builtIn?: boolean;
}

export async function getLoganFeedbackAnalytics(): Promise<LoganFeedbackMetric[]> {
  const result = await api.get<any, { analytics?: LoganFeedbackMetric[] }>("/v1/api/product-assistant/analytics/feedback");
  return result.analytics || [];
}

export async function getLoganKnowledge(): Promise<LoganKnowledgeArticle[]> {
  const result = await api.get<any, { knowledge?: LoganKnowledgeArticle[] }>("/v1/api/product-assistant/knowledge");
  return result.knowledge || [];
}

export async function saveLoganKnowledge(article: LoganKnowledgeArticle): Promise<void> {
  await api.post("/v1/api/product-assistant/knowledge", article);
}

export async function archiveLoganKnowledge(id: number): Promise<void> {
  await api.delete(`/v1/api/product-assistant/knowledge/${id}`);
}

export async function createSupportTicket(
  conversationId: string,
  subject: string,
  description: string,
  pageContext: { route: string; module: string; entityId: string }
): Promise<AssistantReply> {
  const result = await api.post<any, ApiResult<AssistantReply>>(
    `/v1/api/product-assistant/conversations/${conversationId}/tickets`,
    { subject, description, pageContext }
  );
  if (!result.reply) throw new Error("The support ticket was not created.");
  return result.reply;
}
